import {
  fetchIamCustomRoleDefinitionAndCache,
  fetchIamScopeDefinitionAndCache,
  logout,
  retrySilentSignInWhenFailToRenewFn,
  setAuthValues,
  setAuthValuesAndWait,
  setMainValues,
  setValues,
  setValuesInternal,
  updateAccessTokenFn,
} from '../../framework/actions'
import { cache, initCache, LIB_TOKENS_KEY, OIDC_SESSION_KEY } from '../../framework/cache'
import {
  AIOPS_APP_ID,
  API_GW_GLOBAL_SVC_URL_KEY,
  CN_REGION,
  COMMON_SERVICES_MICRO_APP_NAME,
  COSMOS_URL_KEY,
  EMPTY_SAML_DATA,
  FAWKES_APP_ID,
  FAWKES_MGMT_PATH_PREFIX_REGEX,
  HUB_URL_KEY,
  MSP_MICRO_APP_NAME,
  MSP_PATH_PREFIX,
  PA_DEFAULT_ROUTE,
  PAE_APP_ID,
  PANW_DOMAIN,
  PROP_FAWKES_FEATURES,
  PROP_INSIGHTS_FQDN,
  RUNNING,
  SASE_CN_CLIENT_ID,
  SCM_APP_ID,
  TSG_ID,
  TSG_ID_NONE,
  TSG_ID_REGEX,
  US_REGION,
} from '../../framework/constants'
import { getDatadogRum } from '../../framework/externals'
import { clearLoadingMask, showLoadingMask } from '../../framework/loadingMask'
import { hasMicroApp } from '../../framework/microApps'
import preference, {
  COMMON_THEME,
  COMMON_FAVORITES,
  COMMON_LAST_VISITED_TSG_ID,
  COMMON_LAST_VISITED_URL,
} from '../../framework/preference'
import {
  authed,
  authenticated,
  authorized,
  loaded,
  ensureHttpsOrigin,
  getAuthState,
  getFrameworkServiceUrl,
  getFrameworkVars,
  getMainState,
  getState,
  stateRef,
  history,
  isOidcClient,
} from '../../framework/states'
import {
  cleanupTsgIdFromUrl,
  fetchIamRolePermissionDefinition,
  fetchTsgInstances,
  fetchTsgProperties,
  fetchTsgs,
  fetchFeatureFlags,
  getCurrentTsgId,
  getTsgIdFromUrl,
  getTsgToken,
  hasTsgSupport,
  indexTsgTree,
  jwtDecodeCached,
  parseAccess,
  setCurrentTsgId,
  setDatadogGlobalContext,
  tryGetUserManagerWithToken,
  tsgUserManagers,
  fetchSingleTsg,
} from '../../framework/tsg'
import {
  createTimeoutPromise,
  dedupPromise,
  isNonemptyArray,
  isNonemptyObject,
  isString,
  noop,
  wait,
  uniq,
  setTimeout,
  ensureArray,
  isEmptyObject,
  tryParseJson,
  tryParseErrorJson,
  deepFreeze,
  indexArray,
} from '../../framework/utils'
import { hasCustomRolesAccess } from '../../framework/access'
import { _T } from '../../i18n'
import { __error, __info, __isLocalhost, __warn } from '../../init/debug'
import { _performance, _timeouts, _timing } from '../../init/performance'
import fetch from '../../libs/fetch'
import {
  disableTiming,
  addDataDogTimingOnce,
  alertAndDisableTiming,
  clearOidcSessionStorage,
} from '../common/utils'
import {
  clearUserSessionForLogout,
  fetchFeatures,
  fetchFqdnInfo,
  fetchLicenseInfo,
  fetchLoggedInUser,
  getFawkesInst,
  getMigrationSatusPromise,
  getNotificationsPromise,
  getSubTenantInfoPromise,
  hasRunningFawkesInst,
  isFawkesAppId,
  isPanoramaManagedTenant,
  saveSession,
  unsetSession,
} from './fawkesUtils'
import StartupManager, { FAWKES_CONFIG } from './StartupManager'
import { getShellOverrideConfig } from './navUtils'
import { sendLoginMessage } from '../auth/useLogout'
import { getDefaultRoute } from './defaultRedirect'
import { ffUseNav3, handleFallbackForTsgProperties } from '../../framework/extensions/utils'
import { indexFeatureFlags, type Association, type ReadonlyTsgInstances } from '../../framework/indexedArrays'

import type { TsgUserManager } from '../../framework/tsg'
import type {
  Authed,
  MainState,
  AuthState,
  FawkesAuthState,
  AuthInfo,
  SupportAccount,
  UserState,
  OidcState,
  FrameworkVars,
  ConfigLocation,
  ReadonlyTsgProperties,
  Preferences,
  LibTokens,
  ReadonlyFeatureFlags,
  FeatureFlag,
  FavoritePreference,
} from '../../framework/stateTypes'

interface SessionError extends Error {
  code?: number
  cause?: any
  logout?: boolean
}

type TsgPromises = {
  tsgPropertiesPromise?: Promise<ReadonlyTsgProperties>
  libTokensPromise: Promise<LibTokens>
}

const getOidcSessionData = async (oidc_config_url: string): Promise<Partial<UserState>> => {
  const oidcCfgUrl = new URL(oidc_config_url)
  const sessionDataUrl = new URL('/am/json/sessions/?_action=getSessionProperties', oidcCfgUrl.origin).toString()
  const oidcSessionData = await fetch(sessionDataUrl, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    timeout: _timeouts.authApiFetchTimeout,
    headers: {
      'Accept-API-Version': 'resource=3.1',
      'Content-Type': 'application/json',
    },
  }).then((res) => {
    if (res.ok) {
      return res.json().catch((error: Error) => {
        console.error('failed to parse json data for oidc session', error)
        const err = new Error('Failed to parse oidc saml session data') as SessionError
        err.cause = error
        err.logout = true
        throw err
      })
    }
    return res.json().catch(async () => new Error(`get session failed: ${await res.text()}`)).then((error: SessionError) => {
      if (error.code === 401 && !window.location.hostname.endsWith(`.${PANW_DOMAIN}`)) {
        console.error(`Failed to get oidc saml session data probably due to you logged out or 3rd party cookie been blocked, it will also block fetch iam tokens!\nPlease either add "${window.location.hostname}" to the sites that can always use cookies including third-party cookies, or allow all cookies in your browser.`, error)
        if (!__isLocalhost) {
          return EMPTY_SAML_DATA
        }
        console.error('For local development, if you require saml session data, please follow unblock 3rd party cookie, and ensure properly logged in.\nIf you do not require those data, you can turn it off by unset the flag')
      }
      console.error('failed to get oidc saml session data', error)
      const err = new Error('Failed to get oidc saml session data') as SessionError
      err.cause = error
      err.logout = true
      throw err
    })
  }, (err: SessionError) => {
    console.error('failed to get oidc saml session data', err)
    err.logout = true
    throw err
  })
  const { supportaccountids, notonorafter, accesstoken, pingcert } = oidcSessionData || {}
  const isEmptyStr = (str: any) => str === '' || str === '""'
  if (isEmptyStr(supportaccountids) &&
    (isEmptyStr(notonorafter) || notonorafter > 0) &&
    (accesstoken === '""' || accesstoken.length > 10) &&
    (pingcert === '""' || pingcert.length > 10)) {
    // user without any support account
    return { ...EMPTY_SAML_DATA } // clone to bypass use cache check
  }
  let cspNotAvailable: boolean | undefined
  if (!supportaccountids || !notonorafter) {
    cspNotAvailable = true
  }
  let supportAccountIds = []
  if (supportaccountids) {
    try {
      const parsed = JSON.parse(supportaccountids)
      supportAccountIds = Array.isArray(parsed) ? parsed : []
    }
    catch (e) {
      console.error('failed to parse support account ids', supportaccountids)
      const err = new Error('Failed to parse oidc saml session data') as SessionError
      err.cause = e
      err.logout = true
      throw err
    }
  }
  return { supportaccountids, supportAccountIds, notonorafter, accesstoken, pingcert, cspNotAvailable }
}

const loadOidcSessionData = (sub: string) => {
  const fxVars = getFrameworkVars()
  if (fxVars.use_provided_tsg_token) {
    return Promise.resolve(EMPTY_SAML_DATA)
  }
  const authority = fxVars.oidc_client?.authority || fxVars.oidc_client?.config_url
  const useCached = () => {
    const oidcSession = cache.session.get(OIDC_SESSION_KEY)
    if (oidcSession?.supportAccountIds?.length && oidcSession.sub === sub && oidcSession.notonorafter * 1000 > Date.now() + 600000) { // 10min
      oidcSession.supportaccountids = JSON.stringify(oidcSession.supportAccountIds)
      return oidcSession
    }
    cache.session.delete(OIDC_SESSION_KEY)
  }
  const fn = () => getOidcSessionData(authority!).then((oidcSession) => {
    if (oidcSession === EMPTY_SAML_DATA) {
      oidcSession = useCached() || EMPTY_SAML_DATA
    }
    else {
      const { supportaccountids, ...data } = oidcSession
      cache.session.set(OIDC_SESSION_KEY, Object.assign(data, { sub }))
    }
    return oidcSession
  }, (err) => {
    const oidcSession = useCached()
    if (oidcSession) {
      return oidcSession
    }
    throw err
  })
  return dedupPromise(`oidc-session-${sub}`, fn, { keepPromiseIn: 10000 })
}

const loadFeatureFlags = async (tsg_id: string): Promise<ReadonlyFeatureFlags | undefined> => {
  const cacheKey = `feature-flag-service:${tsg_id}`
  return fetchFeatureFlags(tsg_id).then((featureFlags) => {
    if (featureFlags.error) {
      const cached = cache.session.get<FeatureFlag[] | undefined>(cacheKey)
      if (cached?.length) {
        __info('return cached feature flags due to error fetching new ones', cached)
        return indexFeatureFlags(cached)
      }
    }
    else if (featureFlags?.length) {
      cache.session.set(cacheKey, featureFlags)
    }
    return featureFlags
  })
}

const fetchLibTokens = async (jwt?: string): Promise<LibTokens> => cache.get(LIB_TOKENS_KEY).catch(noop).then((cachedToken) => {
  if (cachedToken && cachedToken.cached > Date.now() - 900000) { // 15min ago
    return cachedToken // no fetch needed
  }
  const url = new URL('/ui_tokens/ui', getFrameworkServiceUrl(API_GW_GLOBAL_SVC_URL_KEY)).toString()
  const promise = dedupPromise(url, async () => fetch(url, {
    method: 'GET',
    mode: 'cors',
    credentials: 'omit',
    timeout: 10000, // 10s
    headers: {
      Authorization: `Bearer ${jwt || await getTsgToken(TSG_ID_NONE)}`,
    }
  }).then(async (resp) => {
    if (resp.ok) {
      return resp.json()
    }
    const data = await resp.text().then(tryParseErrorJson)
    const err = new Error(`${resp.status}: failed to get lib tokens from api gw`)
    Object.defineProperty(err, 'cause', { value: data })
    throw err
  }), { keepPromiseIn: 30000 }).then((tokenInfo) => {
    cache.set(LIB_TOKENS_KEY, Object.assign(tokenInfo, { cached: Date.now() }), { ttl: 604800 }) // 1w
    return tokenInfo
  }, (err: Error) => {
    console.error('failed to get lib tokens', err, err.cause)
    return cachedToken || {}
  })
  if (cachedToken) {
    return cachedToken // not wait for promise
  }
  return promise
})

const fetchTsgSnLookup = async (jwt: string | undefined, sn: string, type: string = ''): Promise<string | undefined> => {
  const urlObj = new URL('/mfe/tsg_sn_lookup', getFrameworkServiceUrl(API_GW_GLOBAL_SVC_URL_KEY))
  urlObj.searchParams.set('serial_number', sn)
  urlObj.searchParams.set('license_type', type)
  const url = urlObj.toString()
  const promise = dedupPromise(url, async () => fetch(url, {
    method: 'GET',
    mode: 'cors',
    credentials: 'omit',
    timeout: 10000, // 10s
    headers: {
      Authorization: `Bearer ${jwt || await getTsgToken(TSG_ID_NONE)}`,
    }
  }).then(async (resp) => {
    if (resp.ok) {
      const res = await resp.json()
      if (res.serial_number) {
        return res.tsg_id || undefined
      }
      __error('invalid response for lookup tsg_id by sn', res)
      const err = new Error('failed to lookup tsg_id by sn due to invalid response')
      err.cause = res
      throw err
    }
    const data = await resp.text().then(tryParseErrorJson)
    __error('failed to lookup tsg_id by sn', data)
    const err = new Error(`${resp.status}: failed to lookup tsg_id by sn`)
    Object.defineProperty(err, 'cause', { value: data })
    throw err
  }), { keepPromiseIn: 30000 }).catch(() => {
    return undefined
  })
  return promise
}

const triggerOnAuthed = (resolved: Authed) => {
  const { auth } = resolved
  if (auth) {
    resolved.auth = Object.defineProperties(Object.create(auth), {
      tsg_id: {
        get() {
          return (getAuthState() || auth).tsg_id
        }
      },
      access: {
        get() {
          return (getAuthState() || auth).access
        }
      },
      access_token: {
        get() {
          return (getAuthState() || auth).access_token
        }
      },
      access_token_decoded: {
        get() {
          return (getAuthState() || auth).access_token_decoded
        }
      },
      fawkes_tenant_id: {
        get() {
          return (getAuthState() || auth).fawkes_tenant_id
        }
      },
      __is_init_auth: { value: true },
    })
  }
  return authed.resolve(resolved)
}

const getInitialFavorites = (tsgId?: string): Pick<Preferences, 'tsgs'> | undefined => {
  const favorites = preference.get(COMMON_FAVORITES) as Record<string, any> | undefined
  if (tsgId && favorites?.[tsgId]) {
    const tsgFavorites = favorites[tsgId] as FavoritePreference[]
    const tsgs = {
      ...stateRef.current?.preferences?.tsgs,
      current: {
        tsg_id: tsgId,
        favorites: indexArray(tsgFavorites, 'path'),
      }
    }

    return { tsgs }
  }
}

const getInitialPreferences = (jwt: string, auth: AuthState) => preference.init(jwt).then(() => {
  const theme = preference.get<string>(COMMON_THEME)
  const last_visited_tsg_id = preference.get<string>(COMMON_LAST_VISITED_TSG_ID) || undefined
  const last_visited_url = preference.get<string>(COMMON_LAST_VISITED_URL) || undefined
  const preferences = {} as Record<string, any>
  if (theme && auth.preferences?.user?.theme !== theme) {
    preferences.user = {
      theme,
    }
    sessionStorage.removeItem('spk_user_pref')
    localStorage.spk_user_theme = theme
  }
  if (last_visited_tsg_id && TSG_ID_REGEX.test(last_visited_tsg_id)) {
    preferences.user = {
      ...preferences.user,
      last_visited_tsg_id,
    }
  }
  if (isString(last_visited_url) && last_visited_url.startsWith(window.location.origin)) {
    const tsgId = new URL(last_visited_url).searchParams.get(TSG_ID)
    if (!tsgId || (last_visited_tsg_id && tsgId === last_visited_tsg_id)) {
      preferences.user = {
        ...preferences.user,
        last_visited_url,
      }
    }
  }
  Object.assign(preference, getInitialFavorites(auth?.tsg_id))
  if (!isEmptyObject(preferences)) {
    setValuesInternal({ preferences })
  }
  return preferences
})

export const generateAuthInfoForTsg = ({ tsg_id, instances }: { tsg_id: string, instances: ReadonlyTsgInstances }, { notonorafter, supportAccountIds }: { notonorafter: string, supportAccountIds: SupportAccount[] }): AuthInfo | undefined => {
  const hubUrl = getFrameworkServiceUrl(HUB_URL_KEY)
  const fxVars = getFrameworkVars()
  const fawkesInstances = instances?.filter(inst => inst.status === RUNNING && isFawkesAppId(inst.app_id))
  if (!fawkesInstances?.length) {
    console.warn('no fawkes instance found')
    return
  }
  if (fawkesInstances.length > 1) {
    console.warn('found more than 1 fawkes intances from the tsg', fawkesInstances)
    if (fxVars.fawkes_session) {
      alertAndDisableTiming('Multiple Fawkes instances in the same TSG are not supported')
      return
    }
  }
  // make sure fawkes first
  const [cspId, hasMoreCspId] = Array.from(fawkesInstances.concat(instances).reduce((set, inst) => {
    if (isFawkesAppId(inst.app_id) && +inst.support_account_id) {
      set.add(+inst.support_account_id)
    }
    return set
  }, new Set())) as number[]
  if (!cspId) {
    console.warn('no csp id found')
    return
  }
  if (hasMoreCspId) {
    console.warn('found multiple csp ids from tsg instances', tsg_id, cspId, hasMoreCspId)
    alertAndDisableTiming('Multiple Fawkes instances from different accounts in the same TSG are not supported')
  }
  const existing = new Set(supportAccountIds.map(acc => +acc.accountid))
  const needed = uniq(instances.map(t => +t.support_account_id)).filter(id => id && !existing.has(id))
  if (needed.length) { // fake support accounts for token service
    supportAccountIds = supportAccountIds.concat(needed.map(accountid => ({
      accountid,
      name: ' ',
      membershipStatus: 0,
      signature: '==', // faked
      roles: [],
      isFromTsg: true,
    })))
  }
  const rbacRoles = instances.reduce((o, inst) => {
    const app = o[inst.app_id] || (o[inst.app_id] = {})
    app[inst.tenant_id] = { 'Instance Admin': true }
    return o
  }, {} as Record<string, any>)
  let hasAccount = false
  if (supportAccountIds?.length && cspId > 0) {
    for (const acc of supportAccountIds) {
      if (+acc.accountid === cspId) {
        hasAccount = true
        acc.rbacRoles = rbacRoles
      }
      else {
        acc.rbacRoles = {}
      }
    }
  }
  if (!hasAccount) {
    console.info('allow load fawkes without the csp account access', cspId)
  }
  const entitlements = instances.reduce((ent, inst) => {
    const app = ent[inst.app_id] || (ent[inst.app_id] = {
      app_id: inst.app_id,
      enabled: true,
      entitled: true,
      tenants: []
    })
    const {
      tenant_id,
      associations,
      provisioning_status,
      status: instance_status = provisioning_status,
      license_expiration: expiration,
      region,
      serial_number,
      entitlements,
      url,
    } = inst
    app.tenants.push({
      tenant_id,
      associations: isNonemptyArray(associations) ? associations!.reduce((o, asso) => {
        o[asso.app_id] = asso
        return o
      }, {} as Record<string, Association>) : {},
      instance_status,
      expiration,
      region,
      serial_number,
      entitlements,
      url,
    })
    return ent
  }, {
    'App Portal': {
      support_account_id: cspId,
      rbac_roles: rbacRoles,
      tsg_id,
      url: hubUrl,
    },
  } as Record<string, any>)
  const appPortalUrl = new URL('/hub', hubUrl)
  appPortalUrl.searchParams.set(TSG_ID, tsg_id)
  const authInfo = {
    credentials: {
      notonorafter,
      supportAccountIds,
    },
    entitlements,
    extraInfo: {
      appPortal: appPortalUrl.toString(),
    }
  } as AuthInfo
  __info('generated authInfo', authInfo)
  return authInfo
}

const infoLoadedWithoutFawkes = async (auth: AuthState, tsgAuthInfo?: AuthInfo) => {
  const main = { ...stateRef.current.main, isLoggedIn: true } as MainState

  const remove_main_state = auth.__remove_main_state ??
    (getFrameworkVars().remove_main_state === true && !isOidcClient(SASE_CN_CLIENT_ID))
  if (remove_main_state) {
    Object.assign(main, {
      isLoggedIn: true,
      infoLoaded: true,
      configLocation: {} as ConfigLocation,
      loggedInUser: undefined,
      licenseInfo: undefined,
      tenant: undefined,
      authInfo: undefined
    })
  }
  else {
    const {
      email,
      // username: userName,
      firstname,
      lastname,
      supportaccountids = '[]',
      supportAccountIds = [],
      notonorafter,
      accesstoken,
      pingcert,
    } = auth.user!
    const { private_site_origin } = getFrameworkVars()
    const tenant = private_site_origin ? undefined : {
      type: FAWKES_CONFIG,
      info: {},
    } as any
    const fawkesInst = getFawkesInst(auth.instances)
    if (fawkesInst) {
      const {
        tenant_id: tenantId,
        support_account_id: supportAccountId,
        support_account_name: accountName,
        tsg_id: sgId,
        iam_controlled: iamControlled,
        state,
        runtime_attributes: {
          paas_api_url,
          admin_api_url,
        } = {},
      } = fawkesInst
      Object.assign(tenant, {
        id: tenantId,
        tenantId,
        sgId,
        supportAccountId,
        accountName,
        info: fawkesInst,
        type: isPanoramaManagedTenant(auth),
        state,
        iamControlled,
      })
      const fawkes_paas_api_url = ensureHttpsOrigin(paas_api_url)
      const fawkes_admin_api_url = ensureHttpsOrigin(admin_api_url)
      Object.assign(auth, {
        fawkes_tenant_id: tenantId,
        fawkes_paas_api_url,
        fawkes_admin_api_url,
      })
      if (fawkes_admin_api_url) {
        window.__admin_cluster_url = fawkes_admin_api_url // update global just in case
      }
    }
    const res = private_site_origin ? undefined : auth.access?.find(scope => scope.app_id === FAWKES_APP_ID) || auth.access?.find(scope => !scope.app_id)
    const roles = res?.aggRoleNames || res?.roleNames || []
    main.loggedInUser = {
      email,
      userName: email,
      supportaccountids,
      supportAccountIds,
      profile: {
        email,
        firstname,
        lastname,
        displayName: '',
        accesstoken,
        groups: '""',
        cert: '',
        supportaccountids,
        notonorafter,
        pingcert,
        // 'block': '0',
      },
      tenant,
      roles,
      preference: {},
      extra: {},
    }
    main.tenant = tenant
    main.authInfo = tsgAuthInfo
  }
  const region = auth.instances?.getPlatformRegion?.()
  const cosmosUrl = region && auth.fawkes_region_wo_api && getFrameworkServiceUrl(COSMOS_URL_KEY, { region })
  if (cosmosUrl) {
    main.featuresInfo = [{ name: 'pai', toggle: 1 }]
    main.fqdnInfo = {
      msg: {
        paifqdn: new URL(cosmosUrl).hostname,
        http_code: 200,
        mocked: true,
      }
    }
  }

  const promises: Promise<any>[] = []
  const waitExtraPromises = (...args: any[]): Promise<any[]> => {
    const addPromises = args.flat(1).filter(p => p.then)
    if (addPromises.length) {
      const [timeoutPromise, clear] = createTimeoutPromise(15000, 'waitExtraPromises 15s timeout')
      const promise = Promise.race([
        Promise.all(addPromises).then((res) => {
          clear()
          return res
        }),
        timeoutPromise.catch(() => [] as any[]),
      ])
      promises.push(promise)
      return promise
    }
    return Promise.resolve([])
  }
  const resolvedMain = Object.defineProperties(Object.create(main), { main: { value: main } })
  const resolvedMainFn = () => Promise.resolve(resolvedMain)
  const resolved = {
    main,
    auth,
    loaded,
    getFeatures: resolvedMainFn,
    getFqdnInfo: resolvedMainFn,
    getLicenseInfo: resolvedMainFn,
    getSubTenantInfo: resolvedMainFn,
    getAuthInfo: resolvedMainFn,
    getNotifications: resolvedMainFn,
    getMigrationStatus: resolvedMainFn,
    getTokenManager: undefined,
    waitExtraPromises,
    StartupManager,
    getAuthState,
    getMainState,
    getState,
    setAuthValues,
    setMainValues,
    setValues,
  }
  if (!auth.apps && stateRef.current.auth?.apps?.length) {
    auth.apps = stateRef.current.auth?.apps
  }
  setValuesInternal({ main, auth })
  triggerOnAuthed(resolved) // ensure trigger onAuthed even without fawkes
  await authed.catch(noop) // ignore rejection
  if (promises.length) {
    await Promise.allSettled(promises)
  }

  main.infoLoaded = true
  setMainValues({ infoLoaded: true })

  loaded.resolve(main)
}

const loadPartialMainInfo = async (auth: AuthState, { libTokensPromise, tsgPropertiesPromise }: TsgPromises) => {
  const starts = Date.now()
  const { main: mainState } = stateRef.current
  const main = {
    ...mainState,
    isLoggedIn: true,
    infoLoaded: false,
    configLocation: {} as ConfigLocation,
    loggedInUser: undefined,
    licenseInfo: undefined,
    tenant: undefined,
    authInfo: undefined
  } as MainState
  const methodPromises = {} as Record<string, Promise<MainState> | undefined>
  const resolvedMain = Object.defineProperties(Object.create(main), { main: { value: main } })
  const fxVars = getFrameworkVars()
  if (libTokensPromise) {
    methodPromises.tokenInfo = libTokensPromise.then(() => resolvedMain)
  }
  const mfeProps = tsgPropertiesPromise ? await tsgPropertiesPromise : undefined
  const fawkesFeatures = mfeProps?.get(PROP_FAWKES_FEATURES)?.toggles
  if (Array.isArray(fawkesFeatures)) {
    main.featuresInfo = fawkesFeatures.map(({ fn, name = fn, t, toggle = t }) => ({ name, toggle }))
    methodPromises.featuresInfo = Promise.resolve(resolvedMain)
  }
  else if (mfeProps && !auth?.instances?.has(PAE_APP_ID) &&
    [AIOPS_APP_ID, SCM_APP_ID].some(appId => auth?.instances?.has(appId))) {
    main.featuresInfo = []
    methodPromises.featuresInfo = Promise.resolve(resolvedMain)
  }
  else if (mfeProps && fxVars.skip_fawkes_features_fallback) {
    const msg = 'fawkes features not found in mfe prpoerty but skipped fallback api calls'
    console.warn(msg)
    getDatadogRum()?.addError(new Error(`${msg} for tsg ${auth.tsg_id}`), {
      type: 'client_error',
      tsg_id: auth.tsg_id,
    })
    main.featuresInfo = []
    methodPromises.featuresInfo = Promise.resolve(resolvedMain)
  }
  const insightsFqdn = mfeProps?.get(PROP_INSIGHTS_FQDN)
  if (insightsFqdn) {
    const insightsFqdnValue = insightsFqdn.url || insightsFqdn.fqdn
    const insightsFqdnUrl = insightsFqdnValue.startsWith('https:') ?
      new URL(insightsFqdnValue).hostname : insightsFqdnValue
    main.fqdnInfo = {
      msg: {
        paifqdn: insightsFqdnUrl,
        http_code: 200,
        mocked: true,
      }
    }
    methodPromises.fqdnInfo = Promise.resolve(resolvedMain)
  }
  else {
    const region = auth?.instances?.getPlatformRegion?.()
    const cosmosUrl = region && getFrameworkServiceUrl(COSMOS_URL_KEY, { region })
    // skip gpFQDN API in:
    // 0. if mfe prop insights.fqdn exist (above else)
    // 1. skip_gpfqdn_for_insights flag
    // 2. non-US region, region mapping is enough
    // 3. for non-PA tenants, there are no staging envs
    if (cosmosUrl && (fxVars.skip_gpfqdn_for_insights || region !== US_REGION || !auth?.instances?.has(PAE_APP_ID))) {
      main.fqdnInfo = {
        msg: {
          paifqdn: new URL(cosmosUrl).hostname,
          http_code: 200,
          mocked: true,
        }
      }
      methodPromises.fqdnInfo = Promise.resolve(resolvedMain)
    } // only us region need to check gpFQDN API again
  }
  if (!methodPromises.featuresInfo || !methodPromises.fqdnInfo) {
    _timing.fawkes_main_info_load_start = starts
  }

  const collected = {
    cacheUsed: false,
    hasError: false,
  }

  const tenantId: string | undefined = auth?.instances?.get(FAWKES_APP_ID)?.tenant_id || undefined

  const abortController = new AbortController()
  const fetchPromiseOption = { signal: abortController.signal, tsg_id: auth.tsg_id }

  const getFqdnInfo = async (): Promise<MainState> => {
    if (methodPromises.fqdnInfo) {
      return methodPromises.fqdnInfo
    }
    const cacheKey = `gp-fqdn-${tenantId}`
    const fetchFqdn = () => fetchFqdnInfo(fetchPromiseOption).then((fqdnInfo) => {
      if (fqdnInfo?.msg?.paifqdn && !abortController.signal.aborted) {
        main.fqdnInfo = {
          msg: {
            paifqdn: fqdnInfo.msg.paifqdn,
            http_code: 200,
            temp: true,
          }
        }
        cache.set(cacheKey, fqdnInfo, { ttl: 86400 }) // 1d
      }
      return resolvedMain
    }, (err: Error) => {
      if (abortController.signal.aborted) {
        return resolvedMain
      }
      collected.hasError = true
      if (cached?.msg?.paifqdn) {
        main.fqdnInfo = {
          msg: {
            paifqdn: cached.msg.paifqdn,
            http_code: 200,
            temp: true,
          }
        }
        collected.cacheUsed = true
        console.error('failed to get fqdn use cache instead', err, err.cause)
      }
      else {
        console.warn('failed to get fqdn info may due to no pa license ready yet, use fallback cosmos fqdn instead', err)
        const region = auth?.instances?.getPlatformRegion?.()
        const cosmosUrl = region && getFrameworkServiceUrl(COSMOS_URL_KEY, { region })
        if (cosmosUrl) {
          main.fqdnInfo = {
            msg: {
              paifqdn: new URL(cosmosUrl).hostname,
              http_code: 200,
              mocked: true,
            }
          }
        }
      }
      return resolvedMain
    }) as Promise<MainState>
    const cached = await cache.get(cacheKey)
    if (cached?.msg?.paifqdn) {
      main.fqdnInfo = {
        msg: {
          paifqdn: cached.msg.paifqdn,
          http_code: 200,
          temp: true,
        }
      }
      collected.cacheUsed = true
      methodPromises.fqdnInfo = Promise.resolve(resolvedMain)
      fetchFqdn()
    }
    else {
      methodPromises.fqdnInfo = fetchFqdn()
    }
    return methodPromises.fqdnInfo
  }

  const getFeatures = (): Promise<MainState> => {
    if (methodPromises.featuresInfo) {
      return methodPromises.featuresInfo
    }
    const cacheKey = `features-${tenantId}`
    const fetchFeaturesFn = () => fetchFeatures(fetchPromiseOption).then((features) => {
      if (!abortController.signal.aborted) {
        main.featuresInfo = features
      }
      if (tenantId && isNonemptyArray(features)) {
        cache.session.set(cacheKey, features)
      }
      return resolvedMain
    }, (err: Error) => {
      if (abortController.signal.aborted) {
        return resolvedMain
      }
      console.error('failed to get features', err, err.cause)
      collected.hasError = true
      if (cached) {
        main.featuresInfo = cached
        collected.cacheUsed = true
        return resolvedMain
      }
      // throw err
    }) as Promise<MainState>
    const cached = tenantId ? cache.session.get<MainState['featuresInfo']>(cacheKey) : undefined
    if (cached) {
      main.featuresInfo = cached
      collected.cacheUsed = true
      methodPromises.featuresInfo = Promise.resolve(resolvedMain)
      fetchFeaturesFn()
    }
    else {
      methodPromises.featuresInfo = fetchFeaturesFn()
    }
    return methodPromises.featuresInfo
  }

  const promises = [
    getFeatures(),
    getFqdnInfo(),
  ] as Promise<any>[]

  const resolved: Partial<Authed> = { main, auth, loaded }

  const resolvedMainFn = () => Promise.resolve(resolvedMain)

  const waitExtraPromises = ffUseNav3() ? () => {
    console.error('waitExtraPromises is no longer supported in nav v3')
  } : (...args: any[]): Promise<any[]> => {
    const addPromises = args.flat(1).filter(p => p.then)
    if (addPromises.length) {
      const [timeoutPromise, clear] = createTimeoutPromise(15000, 'waitExtraPromises 15s timeout')
      const promise = Promise.race([
        Promise.all(addPromises).then((res) => {
          clear()
          return res
        }),
        timeoutPromise.catch(() => [] as any[]),
      ])
      promises.push(promise)
      return promise
    }
    return Promise.resolve([])
  }

  Object.assign(resolved, {
    getFeatures,
    getFqdnInfo,
    getLicenseInfo: resolvedMainFn,
    getSubTenantInfo: resolvedMainFn,
    getAuthInfo: resolvedMainFn,
    getNotifications: resolvedMainFn,
    getMigrationStatus: resolvedMainFn,
    getTokenManager: undefined,
    waitExtraPromises,
    StartupManager,
    getAuthState,
    getMainState,
    getState,
    setAuthValues,
    setMainValues,
    setValues,
  })

  setValuesInternal({ main: Object.freeze(Object.create(main)) })
  triggerOnAuthed(resolved as Authed)
  await authed.catch(noop) // ensure other micro-app wait for authed executed

  if (promises.length) { // await for the promises with timeout
    let ts: number
    const timeoutPromise = new Promise((resolve) => {
      ts = setTimeout(() => {
        resolve({
          error: new Error('Loading tenant information timeout occurred. Please try again.')
        })
      }, _timeouts.fawkesMainFetchTimeout) // 16s
    })
    const res: { error?: Error } = await Promise.race<object>([
      timeoutPromise,
      Promise.allSettled(promises).then(() => {
        clearTimeout(ts)
        return {}
      }),
    ])
    const error: Error | undefined = res.error || undefined
    if (error) {
      clearLoadingMask()
      setValuesInternal({
        loginFailed: {
          message: error.message,
          reload: true,
        }
      })
      return
    }
  }
  if (!main.featuresInfo) {
    main.featuresInfo = []
  }
  main.infoLoaded = true
  const frozenMain = Object.freeze(Object.assign(Object.create(main), main))
  setValuesInternal({ main: frozenMain })

  if (_timing.fawkes_main_info_load_start) {
    const end = Date.now()
    const duration = end - starts
    let __log = __info
    if (duration > 25000 || collected.hasError) {
      __log = __error
    }
    else if (duration > 5000) {
      __log = __warn
    }
    _timing.fawkes_main_info_load_duration = duration
    _timing.fawkes_main_info_load_end = end
    __log(`fawkes main info loaded in ${duration}ms ${collected.cacheUsed ? 'with cache used' : 'without cache'}`, main)
    addDataDogTimingOnce('fawkes_main_info_loaded', end)
    addDataDogTimingOnce('fawkes_main_info_load_duration', duration)
  }
  loaded.resolve(main)
}

export const loadFawkesMainInfo = async (tsgAuthInfo: AuthInfo, { libTokensPromise }: TsgPromises) => {
  const starts = Date.now()
  _timing.fawkes_main_info_load_start = starts
  const { auth, main: mainState, leftMainNavigationPromise } = stateRef.current
  const main = { ...mainState, isLoggedIn: true } as MainState
  const fxVars = getFrameworkVars()
  const resolvedMain = Object.defineProperties(Object.create(main), { main: { value: main } })
  const methodPromises = {} as Record<string, Promise<MainState> | undefined>
  const promises: Promise<any>[] = []
  const critialErrors = [] as Error[]
  const collected = {
    cacheUsed: false,
    hasError: false,
  }
  const abortController = new AbortController()
  const fetchPromiseOption = { signal: abortController.signal, tsg_id: auth.tsg_id }
  const fawkesWithoutPA = hasTsgSupport() && auth?.instances && (
    auth.instances.get(FAWKES_APP_ID)?.runtime_attributes?.hidden ||
    !auth.instances.some(inst => inst.app_id.startsWith(FAWKES_APP_ID))
  )
  let tenantId: string | undefined = cache.session.get(`fawkes-user-${auth.tsg_id}`)?.tenant?.tenantId || undefined
  if (libTokensPromise) {
    methodPromises.tokenInfo = libTokensPromise.then((tokenInfo) => {
      main.tokenInfo = tokenInfo
      return resolvedMain
    })
  }
  const getTokenInfo = (): Promise<MainState> => {
    return methodPromises.tokenInfo || resolvedMain
  }
  const getFqdnInfo = (): Promise<MainState> => {
    if (methodPromises.fqdnInfo) {
      return methodPromises.fqdnInfo
    }
    const cacheKey = `gp-fqdn-${tenantId}`
    const cached = cache.session.get(cacheKey)
    const fetchFqdn = () => fetchFqdnInfo(fetchPromiseOption).then((fqdnInfo) => {
      if (fqdnInfo && !abortController.signal.aborted) {
        main.fqdnInfo = fqdnInfo
      }
      if (main.fqdnInfo) {
        cache.session.set(cacheKey, main.fqdnInfo)
      }
      return resolvedMain
    }, (err: Error) => {
      if (abortController.signal.aborted) {
        return resolvedMain
      }
      if (fawkesWithoutPA) {
        console.info('failed to get fqdn info may due to no pa license ready yet, use fallback cosmos fqdn instead', err)
        const region = auth?.instances?.getPlatformRegion?.()
        const cosmosUrl = region && getFrameworkServiceUrl(COSMOS_URL_KEY, { region })
        if (cosmosUrl) {
          main.fqdnInfo = {
            msg: {
              paifqdn: new URL(cosmosUrl).hostname,
              http_code: 200,
              mocked: true,
            }
          }
        }
        return resolvedMain
      }
      collected.hasError = true
      if (cached) {
        main.fqdnInfo = cached
        collected.cacheUsed = true
        console.error('failed to get fqdn use cache instead', err, err.cause)
        return resolvedMain
      }
      const error = new Error('Failed to fetch required tenant data, please try reload.')
      error.cause = err
      critialErrors.push(error)
      console.error('failed to get fqdn', err)
      // throw err
    }) as Promise<MainState>
    if (cached) {
      main.fqdnInfo = cached
      collected.cacheUsed = true
      methodPromises.fqdnInfo = Promise.resolve(resolvedMain)
      fetchFqdn()
    }
    else {
      methodPromises.fqdnInfo = fetchFqdn()
      promises.push(methodPromises.fqdnInfo)
    }
    return methodPromises.fqdnInfo
  }
  const getFeatures = (): Promise<MainState> => {
    if (methodPromises.featuresInfo) {
      return methodPromises.featuresInfo
    }
    const cacheKey = `features-${tenantId}`
    const cached = tenantId ? cache.session.get<MainState['featuresInfo']>(cacheKey) : undefined
    const fetchFeaturesFn = () => fetchFeatures(fetchPromiseOption).then((features) => {
      if (!abortController.signal.aborted) {
        main.featuresInfo = features
      }
      if (tenantId && isNonemptyArray(features)) {
        cache.session.set(cacheKey, features)
      }
      return resolvedMain
    }, (err: Error) => {
      if (abortController.signal.aborted) {
        return resolvedMain
      }
      console.error('failed to get features', err, err.cause)
      collected.hasError = true
      if (cached) {
        main.featuresInfo = cached
        collected.cacheUsed = true
        return resolvedMain
      }
      // throw err
    }) as Promise<MainState>
    if (cached) {
      main.featuresInfo = cached
      collected.cacheUsed = true
      methodPromises.featuresInfo = Promise.resolve(resolvedMain)
      fetchFeaturesFn() // TODO: may need to check and update state
    }
    else {
      methodPromises.featuresInfo = fetchFeaturesFn()
      promises.push(methodPromises.featuresInfo)
    }
    return methodPromises.featuresInfo
  }
  const getLicenseInfo = (): Promise<MainState> => {
    if (methodPromises.licenseInfo) {
      return methodPromises.licenseInfo
    }
    const cacheKey = `license-info-${tenantId}`
    const cached = tenantId ? cache.session.get<MainState['licenseInfo']>(cacheKey) : undefined
    const fetchLicenseInfoFn = () => fetchLicenseInfo(fetchPromiseOption).then((licenseInfo) => {
      if (!abortController.signal.aborted) {
        main.licenseInfo = licenseInfo
      }
      if (tenantId && isNonemptyObject(main.licenseInfo)) {
        cache.session.set(cacheKey, main.licenseInfo)
      }
      return resolvedMain
    }, (err: Error) => {
      if (abortController.signal.aborted) {
        return resolvedMain
      }
      if (fawkesWithoutPA) {
        console.info('failed to get license info may due to no pa license ready yet', err)
        return resolvedMain
      }
      collected.hasError = true
      console.error('failed to get license info', err, err.cause)
      if (cached) {
        main.licenseInfo = cached
        collected.cacheUsed = true
        return resolvedMain
      }
      // throw err
    }) as Promise<MainState>
    if (cached) {
      main.licenseInfo = cached
      collected.cacheUsed = true
      fetchLicenseInfoFn() // TODO: may need to check and update state
      methodPromises.licenseInfo = Promise.resolve(resolvedMain)
    }
    else {
      methodPromises.licenseInfo = fetchLicenseInfoFn()
      promises.push(methodPromises.licenseInfo)
    }
    return methodPromises.licenseInfo
  }

  const loadLoggedInUser = () => {
    const cacheKey = `fawkes-user-${auth.tsg_id}`
    const cached = cache.session.get(cacheKey)
    const fetchLoggedInUserFn = () => (
      fetchLoggedInUser(auth.tsg_id).then((user) => {
        cache.session.set(cacheKey, user)
        return user
      })
    )
    if (cached) {
      collected.cacheUsed = true
      fetchLoggedInUserFn() // TODO: may need to check and update state
      return Promise.resolve(cached)
    }
    return fetchLoggedInUserFn()
  }

  const userPromise = loadLoggedInUser().catch((e) => {
    console.error('failed to get logged in user', e, e.cause)
    if (e.cause?.errorMessage) {
      e.errorCode = e.cause.errorCode
      if (e.errorCode?.startsWith?.('prisma access tenant not found')) {
        e.errorMessage = _T('Prisma Access or management is not running in this tenant.')
      }
      else {
        e.errorMessage = e.cause.errorMessage
      }
    }
    throw e
  }) as Promise<{ user?: any, tenant?: any, preference?: any, extra?: any, hasCsrfSalt?: boolean, csrfSalt?: string, error?: string }>

  if (tenantId && fxVars.auto_load_info !== false) {
    getFqdnInfo()
    getFeatures()
    getLicenseInfo()
  }

  const user = await userPromise
  if (!user?.user) {
    abortController.abort()
    clearLoadingMask()
    if (user.error) {
      setValuesInternal({
        loginFailed: {
          message: user.error,
        }
      })
    }
    return
  }
  const {
    // accesstoken,
    email,
    firstname,
    lastname,
    // username: userName,
    supportaccountids = '[]',
    supportAccountIds = [],
    notonorafter,
    accesstoken,
    pingcert,
  } = auth?.user || {}
  main.loggedInUser = {
    supportaccountids,
    supportAccountIds,
    userName: email,
    ...user.user,
    profile: {
      email,
      firstname,
      lastname,
      displayName: '',
      accesstoken,
      groups: '""',
      cert: '',
      supportaccountids,
      notonorafter,
      pingcert,
      ...user.user.profile,
      // 'block': '0',
    },
    tenant: user.tenant,
    preference: user.preference?.params || {},
    extra: user.extra,
  }
  main.tenant = user.tenant
  main.authInfo = tsgAuthInfo
  if (!main.loggedInUser!.supportAccountIds?.length) {
    const cspStr = user.user?.profile?.supportaccountids || supportaccountids
    if (cspStr?.length > 2) {
      main.loggedInUser!.supportAccountIds = tryParseJson(cspStr)
    }
  }
  tenantId = main.tenant?.tenantId as string | undefined
  const cachedFqdnInfo = cache.session.get(`gp-fqdn-${tenantId}`)
  if (cachedFqdnInfo) {
    main.fqdnInfo = cachedFqdnInfo
  }

  const resolved: Partial<Authed> = { main, auth, loaded }
  if (leftMainNavigationPromise) { // wait for micro-app to be loaded
    promises.push(leftMainNavigationPromise.catch(noop))
  }
  const getNotifications = (): Promise<MainState> => {
    if (fxVars.skip_extra_fawkes_fetch) {
      __info('skip get notifications from fawkes')
      main.notificationsInfo = []
      return resolvedMain
    }
    if (methodPromises.notification) {
      return methodPromises.notification
    }
    methodPromises.notification = getNotificationsPromise(fetchPromiseOption).then((notifications) => {
      main.notificationsInfo = notifications
      return resolvedMain
    }, (err: Error) => {
      collected.hasError = true
      console.error('failed to get notifications info', err)
      // throw err
    }) as Promise<MainState>
    promises.push(methodPromises.notification)
    return methodPromises.notification
  }
  const getSubTenantInfo = (): Promise<MainState> => {
    if (fxVars.skip_extra_fawkes_fetch) {
      __info('skip get sub tenant from fawkes')
      return resolvedMain
    }
    if (methodPromises.subTenantInfo) {
      return methodPromises.subTenantInfo
    }
    methodPromises.subTenantInfo = getSubTenantInfoPromise(fetchPromiseOption).then((subTenant) => {
      const itemsStr = subTenant?.msg?.items
      const items = itemsStr && tryParseJson(itemsStr)
      subTenant.isMultiTenancy = items?.length > 1
      if (itemsStr && !Array.isArray(items)) {
        collected.hasError = true
        console.error('failed to parse sub tenant info', itemsStr)
      }
      main.subTenantInfo = subTenant
      return resolvedMain
    }, (err: Error) => {
      collected.hasError = true
      console.error('failed to get sub tenant info', err)
      // throw err
    }) as Promise<MainState>
    promises.push(methodPromises.subTenantInfo)
    return methodPromises.subTenantInfo
  }
  const getMigrationStatus = (): Promise<MainState> => {
    if (fxVars.skip_extra_fawkes_fetch) {
      __info('skip get migration status from fawkes')
      return resolvedMain
    }
    if (methodPromises.migrationStatus) {
      return methodPromises.migrationStatus
    }
    const cacheKey = `fawkes-migration-status-${tenantId}`
    const fetchMigrationStatus = () => getMigrationSatusPromise(fetchPromiseOption).then((migrateStatus) => {
      const {
        state,
        fawkesTenantId,
      } = migrateStatus || {}
      main.migrationStatus = state
      main.migrationNewFawkesTenantId = fawkesTenantId
      if (!state && !fawkesTenantId) { // only cache if no migration status
        cache.session.set(cacheKey, { state })
      }
      else {
        cache.session.delete(cacheKey)
      }
      return resolvedMain
    }, (e) => {
      collected.hasError = true
      console.error('failed to get migration status', e)
    }) as Promise<MainState>
    const cached = cache.session.get(cacheKey)
    if (cached) {
      main.migrationStatus = ''
      collected.cacheUsed = true
      methodPromises.migrationStatus = Promise.resolve(resolvedMain)
      fetchMigrationStatus()
    }
    else {
      methodPromises.migrationStatus = fetchMigrationStatus()
      promises.push(methodPromises.migrationStatus)
    }
    return methodPromises.migrationStatus
  }

  const getAuthInfo = (): Promise<MainState> => resolvedMain
  const waitExtraPromises = (...args: any[]): Promise<any[]> => {
    const addPromises = args.flat(1).filter(p => p.then)
    if (addPromises.length) {
      const [timeoutPromise, clear] = createTimeoutPromise(15000, 'waitExtraPromises 15s timeout')
      const promise = Promise.race([
        Promise.all(addPromises).then((res) => {
          clear()
          return res
        }),
        timeoutPromise.catch(() => [] as any[]),
      ])
      promises.push(promise)
      return promise
    }
    return Promise.resolve([])
  }
  Object.assign(resolved, {
    getFeatures,
    getFqdnInfo,
    getLicenseInfo,
    getSubTenantInfo,
    getAuthInfo,
    getNotifications,
    getMigrationStatus,
    getTokenManager: undefined,
    waitExtraPromises,
    StartupManager,
    getAuthState,
    getMainState,
    getState,
    setAuthValues,
    setMainValues,
    setValues,
  })
  setMainValues(main)
  triggerOnAuthed(resolved as Authed)
  await authed.catch(noop) // ensure other micro-app wait for authed executed

  // defaults
  getTokenInfo()
  if (fxVars.auto_load_info !== false) {
    getFeatures()
    getFqdnInfo()
    getLicenseInfo()
  }

  if (promises.length) {
    let ts: number
    const timeoutPromise = new Promise((resolve) => {
      ts = setTimeout(() => {
        resolve({
          error: new Error('Authentication timeout occurred. Please try again.')
        })
      }, _timeouts.fawkesMainFetchTimeout * 2) // 32s
    })
    const res: { error?: Error } = await Promise.race<object>([
      timeoutPromise,
      Promise.allSettled(promises).then(() => {
        clearTimeout(ts)
        return {}
      }),
    ])
    const error: Error | undefined = res.error ||
      (!hasTsgSupport() && critialErrors[0]) || undefined
    if (error) {
      clearLoadingMask()
      setValuesInternal({
        loginFailed: {
          message: error.message,
          reload: true,
        }
      })
      return
    }
  }

  if (!main.featuresInfo) {
    main.featuresInfo = []
  }

  main.infoLoaded = true
  setValuesInternal({ main })

  const end = Date.now()
  const duration = end - starts
  let __log = __info
  if (duration > 25000 || collected.hasError) {
    __log = __error
  }
  else if (duration > 5000) {
    __log = __warn
  }
  _timing.fawkes_main_info_load_duration = duration
  _timing.fawkes_main_info_load_end = end
  __log(`fawkes main info loaded in ${duration}ms ${collected.cacheUsed ? 'with cache used' : 'without cache'}`, main)
  addDataDogTimingOnce('fawkes_main_info_loaded', end)
  addDataDogTimingOnce('fawkes_main_info_load_duration', duration)
  loaded.resolve(main)
}

const handleIamFawkesSession = async (auth: AuthState, tsgPromises: TsgPromises) => {
  const {
    access_token_decoded,
    instances,
  } = auth
  const tsg_id = auth.tsg_id!
  const access_token = auth.access_token!
  const fxVars = getFrameworkVars()
  unsetSession() // ensure previous tenant id been cleared
  const { supportaccountids, supportAccountIds, notonorafter, accesstoken, pingcert, email, subname = email } = auth.user as UserState
  const sessionData = supportAccountIds && notonorafter ? {
    supportaccountids,
    supportAccountIds,
    notonorafter,
    accesstoken,
    pingcert,
  } : await loadOidcSessionData(subname as string).catch(() => EMPTY_SAML_DATA)
  // always stateless mode, and already checked running status
  const fawkesInst = getFawkesInst(instances)! // isRunningStatus checked
  const fawkesVersion = fawkesInst?.runtime_attributes?.version || null
  const loadParitalMainState = fxVars.remove_main_state && !isOidcClient(SASE_CN_CLIENT_ID) && (fxVars.remove_main_state === true || (
    fawkesVersion && ensureArray(fxVars.remove_main_state).some(ver => fawkesVersion.startsWith(ver))))
  const tsgAuthInfo = loadParitalMainState ? undefined :
    generateAuthInfoForTsg({ tsg_id: tsg_id!, instances }, sessionData)
  if (!tsgAuthInfo && !loadParitalMainState) {
    throw new Error('failed to generate auth info for tsg')
  }
  if (loadParitalMainState && fxVars.remove_main_state === true &&
    fawkesVersion && parseFloat(fawkesVersion.replace('r', '')) < 2024.2) {
    console.error(`The fawkes version '${fawkesVersion}' is no longer supported. As it cannot work while main state removed.`)
  }
  const {
    tenant_id: fawkes_tenant_id,
    runtime_attributes: {
      paas_api_url: fawkes_paas_api_url,
      admin_api_url: fawkes_admin_api_url,
    } = {}
  } = fawkesInst
  const fawkes_region_wo_api = instances.platform_region === CN_REGION && !fawkesInst.runtime_attributes?.admin_api_url
  if (!fawkes_tenant_id || !(fawkes_paas_api_url || fawkes_region_wo_api)) {
    console.error('not able to get valid fawkes instance, cannot find tenant_id or api_url', fawkesInst)
    throw new Error('Failed to initialize Prisma Access management due to missing critical info')
  }
  const fawkesInstanceInfo = {
    fawkes_tenant_id,
    fawkes_paas_api_url,
    fawkes_admin_api_url,
    fawkes_region_wo_api,
    __remove_main_state: loadParitalMainState, // TODO: temp, need cleanup
  } as FawkesAuthState
  if (fawkes_admin_api_url) {
    window.__admin_cluster_url = fawkes_admin_api_url // update global just in case
  }

  const exp = access_token_decoded?.exp || jwtDecodeCached(access_token, tsg_id)?.exp
  const fawkesSession = {
    sub: fawkesInstanceInfo.fawkes_tenant_id,
    token: access_token,
    exp,
  }
  __info(`save fawkes session for ${fawkesSession.sub} exp ${fawkesSession.exp}`)
  saveSession(fawkesSession)

  await setAuthValuesAndWait(Object.assign(auth, { // use await for delay loadMainInfo
    user: {
      ...auth.user,
      ...sessionData,
    },
    ...fawkesInstanceInfo,
  }))

  // wait for the setAuthValues above applied
  if (stateRef.current.auth?.access_token !== access_token) {
    __info('access token not synced, waiting for sync')
    await wait(100)
    if (stateRef.current.auth?.access_token !== access_token) {
      __error('access token is not able to sync to global store auth state:', stateRef.current.auth, auth)
      alertAndDisableTiming('Initialize user session failed, try reload the browser.')
      window.location.reload()
      return
    }
  }

  __info('auth', auth)
  if (fawkes_region_wo_api && !fawkesInst.runtime_attributes) {
    // load without fawkes:
    await infoLoadedWithoutFawkes(auth, tsgAuthInfo)
  }
  else if (loadParitalMainState) {
    __info(`Loading partial main state for fawkes version ${fawkesVersion || 'not found'}`)
    await loadPartialMainInfo(auth, tsgPromises)
  }
  else {
    await loadFawkesMainInfo(tsgAuthInfo!, tsgPromises)
  }
}

const applyDefaultRoute = async (auth: AuthState, tsg_id: string, preferencePromise?: Promise<Record<string, any>>) => {
  const defaultRoute = getDefaultRoute(auth, { initRedirection: true })
  if (defaultRoute) {
    const { pathname } = window.location
    if (pathname === '/') {
      const getLastVisitedPath = async (): Promise<string> => {
        const lastVisitedUrl: string | undefined = (await preferencePromise)?.user?.last_visited_url
        if (lastVisitedUrl) {
          const url = new URL(lastVisitedUrl)
          const tsgIdInUrl = url.searchParams.get(TSG_ID)
          if (!tsgIdInUrl || tsg_id === tsgIdInUrl) {
            url.searchParams.delete(TSG_ID)
            const { pathname, search } = url
            return `${pathname}${search}`
          }
          else if (url.pathname.startsWith(MSP_PATH_PREFIX)) {
            // (auth.tsgs.hasParentOrSubTenants(tsg_id) || !auth.instances.length)?
            return MSP_PATH_PREFIX
          }
        }
        return defaultRoute
      }
      const lastPath = preferencePromise ? await getLastVisitedPath() : defaultRoute
      history.replace(lastPath)
    }
    if (stateRef.current.defaultRoute !== defaultRoute) {
      setValuesInternal({ defaultRoute })
    }
  }
}

export const handleOidcLogin = async (oidc: OidcState) => {
  const starts = Date.now()
  _timing.tsg_tenant_auth_load_start = starts
  let tsg_id: string = ''
  const fxVars = getFrameworkVars()
  const { oidcUser, login } = oidc
  const decodedLoginJwt = jwtDecodeCached(oidcUser.access_token)
  const {
    access_token: defaultTsgToken,
    id_token,
    profile: {
      sub: subname = decodedLoginJwt.sub,
      email,
      name: userName,
      given_name: firstname,
      family_name: lastname,
    } = {}
  } = oidcUser
  if (subname) {
    try {
      getDatadogRum()?.setUser?.({ id: subname })
    }
    catch (e) {
      __error('failed to set datadog user id', e)
    }
    initCache({ user_id: subname, skip: fxVars.pdf_mode })
  }
  const handleAuthorized = async (auth: AuthState) => {
    await loaded
    authorized.resolve(auth)
    await authorized
  }

  const auth = {
    ...stateRef.current.auth,
    loading: false,
    id_token,
    user: {
      subname,
      email,
      username: userName,
      firstname,
      lastname,
    },
  } as AuthState
  const ftPrivateSiteTsgSupport = Boolean(fxVars.private_site_origin && fxVars.private_site_default_tsg && decodedLoginJwt.tsg_id)
  const userManager = oidc.userManager as TsgUserManager
  userManager.events.addUserUnloaded(clearUserSessionForLogout)
  userManager.events.addUserLoaded((tsgUser) => {
    if (stateRef.current.auth?.id_token !== tsgUser.id_token) {
      const update = { // update id token
        id_token: tsgUser.id_token,
      } as AuthState
      if (fxVars.private_site_origin && !ftPrivateSiteTsgSupport) { // update acces_token for private site only
        update.access_token = tsgUser.access_token
        update.access_token_decoded = jwtDecodeCached(tsgUser.access_token)
      }
      setAuthValues(update)
    }
  })
  if (decodedLoginJwt.tsg_id) {
    userManager.tsg_id = decodedLoginJwt.tsg_id
  }

  const libTokensPromise = fetchLibTokens(defaultTsgToken).then(({ cached, ...libTokens }) => {
    setValuesInternal({ libTokens }) // TODO: avoid multiple set values, esp for preferences above
    return libTokens
  })

  if (fxVars.private_site_origin) {
    if (window.location.origin !== fxVars.private_site_origin && !__isLocalhost) {
      console.error('Private site origin is not matched', window.location.origin, fxVars.private_site_origin)
      return setValuesInternal({
        loginFailed: {
          message: 'Private site origin is not matched',
        }
      })
    }
    if (ftPrivateSiteTsgSupport) {
      auth.privateSiteTsgSupport = true
    }
    else {
      Object.assign(auth, {
        initializing: undefined,
        access_token: defaultTsgToken,
        access_token_decoded: decodedLoginJwt,
      })
      authenticated.resolve(auth)
      // load without fawkes if w/o tsg support
      await infoLoadedWithoutFawkes(auth)

      authorized.resolve(auth)
    }
  }

  if (!decodedLoginJwt?.tsg_id) {
    console.error('cannot detect tenant info in the token', decodedLoginJwt, oidcUser)
    return setValuesInternal({
      loginFailed: {
        message: 'cannot detect tenant info in the token',
      }
    })
  }
  if (userManager) {
    if (!tsgUserManagers.__metadata_promise__) {
      const metadataPromise = userManager.settings.metadata ?
        Promise.resolve(userManager.settings.metadata) :
        (userManager.settings as any).metadataService?.getMetadata() // GET /am/oauth2/.well-known/openid-configuration
      Object.defineProperty(tsgUserManagers, '__metadata_promise__', { value: metadataPromise })
    }
    if (!userManager.tsg_id || userManager.tsg_id === TSG_ID_NONE || fxVars.use_provided_tsg_token) {
      tsgUserManagers[TSG_ID_NONE] = userManager // set none user manager as default user manager
    }
    else if (userManager.tsg_id) {
      const errMsg = 'invalid default user manager from oidc'
      __error(errMsg, `tsg_id ${userManager.tsg_id} instead of none`, userManager)
      return setValuesInternal({
        loginFailed: {
          message: errMsg,
        }
      })
    }
    if (subname) {
      tsgUserManagers.__current_user_id__ = subname
      sendLoginMessage(subname)
    }
  }
  else {
    const errMsg = 'not able to find default user manager from oidc'
    __error(errMsg)
    return setValuesInternal({
      loginFailed: {
        message: errMsg,
      }
    })
  }

  const preferencePromise = fxVars.use_user_preferences ? getInitialPreferences(defaultTsgToken, auth) : undefined

  if (fxVars.load_oidc_session || hasMicroApp(COMMON_SERVICES_MICRO_APP_NAME)) {
    auth.sessionDataPromise = loadOidcSessionData(subname || email || '').catch((err) => {
      setValuesInternal({
        loggedInPrompt: {
          title: _T('Warning'),
          message: 'Failed to get your Support Accounts from the session.\nDo you want to reload the browser to try again?\nOtherwise continue without access to certain services.',
          confirmText: _T('Reload'),
          closeText: _T('Continue'),
          onConfirm() {
            const relogin = () => {
              setCurrentTsgId(tsg_id)
              login()
            }
            console.error('force re-login due to failed to get user SAML session data', err)
            showLoadingMask()
            userManager.events.removeUserUnloaded(clearUserSessionForLogout)
            userManager.removeUser().then(relogin, relogin) // will clear session
          }
        }
      }) // continue here, do not block the page load
    })
  }

  const ft_copilot_csp_mode = getShellOverrideConfig()?.['+variables']?.copilot_csp_mode
  let tsgsPromise = !ftPrivateSiteTsgSupport && !ft_copilot_csp_mode && !fxVars.use_provided_tsg_token ? fetchTsgs(defaultTsgToken, email) : undefined
  const defaultProperitesPromise = fxVars.load_mfe_properties ? fetchTsgProperties(TSG_ID_NONE) : undefined
  const rolePermissionsPromise = fetchIamRolePermissionDefinition(defaultTsgToken)
  let snFromUrl: string | undefined
  if (ft_copilot_csp_mode) {
    const { search, pathname } = window.location
    const urlParams = new URLSearchParams(search)
    snFromUrl = urlParams.get('sn')?.replace(/\s/g, '')
    if (snFromUrl) {
      if (urlParams.has('tsg_id')) {
        console.error('sn and tsg_id are mutually exclusive', urlParams.get('tsg_id'), snFromUrl)
        urlParams.delete('tsg_id')
        history.replace({
          pathname,
          search: urlParams.toString(),
        })
      }
      const cacheKey = `sparky:sn:${snFromUrl}:tsg`
      const cachedTsgId = cache.session.get(cacheKey)
      if (cachedTsgId) {
        tsg_id = cachedTsgId
        console.info(`found tsg_id ${tsg_id} for ${snFromUrl} from cache`)
      }
      else {
        const [license_type, serial_number] = snFromUrl.includes(':') ? snFromUrl.split(':', 2) : [undefined, snFromUrl]
        __info('found license_type and serial_number', license_type, serial_number)
        const tsgId = await fetchTsgSnLookup(defaultTsgToken, serial_number, license_type)
        if (tsgId) {
          console.info(`found tsg_id ${tsgId} from serial number ${serial_number} lookup`)
          cache.session.set(cacheKey, tsgId)
          tsg_id = tsgId
        }
        else {
          tsg_id = TSG_ID_NONE
        }
      }
    }
  }
  let tsgFromUrl: string | undefined
  if (!tsg_id) {
    tsgFromUrl = getTsgIdFromUrl()
    tsg_id = tsgFromUrl || getCurrentTsgId() || ''
  }
  if (!tsg_id && preferencePromise && !ftPrivateSiteTsgSupport && !snFromUrl) {
    tsg_id = (await preferencePromise)?.user?.last_visited_tsg_id // can never be none
  }
  if (!tsg_id && ftPrivateSiteTsgSupport && TSG_ID_REGEX.test(fxVars.private_site_default_tsg)) {
    tsg_id = fxVars.private_site_default_tsg
  }
  const reloginForOidcSessionLost = () => {
    const relogin = () => {
      clearOidcSessionStorage() // ensure clear all oidc sessions
      setCurrentTsgId(tsg_id)
      login()
    }
    showLoadingMask()
    userManager.events.removeUserUnloaded(clearUserSessionForLogout)
    userManager.removeUser().then(relogin, relogin) // will clear session
  }
  const [tsgMgr, accessToken] = tsg_id === TSG_ID_NONE ? [userManager, defaultTsgToken] :
    await tryGetUserManagerWithToken(tsg_id).catch(err => {
      if (err?.error === 'interaction_required') {
        console.error('force re-login due to oidc session lost', err)
        reloginForOidcSessionLost()
      }
      throw err
    })
  const tsgTokenDecoded = tsg_id === TSG_ID_NONE ? decodedLoginJwt :
    jwtDecodeCached(accessToken, tsg_id)
  if (snFromUrl) {
    if (tsg_id === TSG_ID_NONE || (tsgMgr.tsg_id && tsgMgr.tsg_id !== tsg_id)) {
      __warn(`try to load tsg ${tsg_id} for sn ${snFromUrl} but no access, fallback to none tsg`)
      tsg_id = TSG_ID_NONE
      auth.access_token = defaultTsgToken
      auth.access_token_decoded = decodedLoginJwt
    }
    else {
      auth.access_token = accessToken
      auth.access_token_decoded = tsgTokenDecoded
    }
  }
  else {
    if (ftPrivateSiteTsgSupport && tsgMgr.tsg_id === tsg_id) {
      tsgsPromise = fetchSingleTsg(tsg_id)
      const tsgs = await tsgsPromise
      if (tsgs.error) {
        tsg_id = fxVars.private_site_default_tsg
        auth.access_token = await getTsgToken(tsg_id)
        auth.access_token_decoded = jwtDecodeCached(auth.access_token, tsg_id)
        if (tsgFromUrl) {
          setValuesInternal({
            loggedInPrompt: {
              title: _T(`Tenant Not Accessible for ${fxVars.private_site_name}`),
              message: `The specified tenant with TSG ID ${tsgFromUrl} seems not available for ${fxVars.private_site_name},\nfallback to load TSG ID ${tsg_id} instead.`,
              closeText: _T('OK'),
            }
          })
        }
      }
      else {
        auth.access_token = accessToken
        auth.access_token_decoded = tsgTokenDecoded
      }
    }
    else {
      auth.access_token = accessToken
      auth.access_token_decoded = tsgTokenDecoded
      if (tsgMgr.tsg_id && tsgMgr.tsg_id !== tsg_id) {
        tsg_id = tsgMgr.tsg_id
        if (tsgFromUrl && tsg_id !== tsgFromUrl) {
          setValuesInternal({
            loggedInPrompt: {
              title: _T('Tenant Not Accessible'),
              message: `You do not have access to the specified tenant with TSG ID ${tsgFromUrl},\nfallback to load TSG ID ${tsg_id} instead.`,
              closeText: _T('OK'),
            }
          })
        }
      }
      if (tsgMgr.tsg_id === tsg_id && !fxVars.use_provided_tsg_token) {
        let needRenew = (Date.now() / 1000) - tsgTokenDecoded.iat > tsgTokenDecoded.expires_in / 5
        if (window.opener) { // via window.open or <a target="_blank"> without rel="noopener"
          // sessionStorage is cloned from opener, clear oidc sessions
          clearOidcSessionStorage()
          needRenew = true
        }
        if (needRenew) {
          __info('force renew tsg token to ensure access up-to-date')
          const tsgUser = await tsgMgr.signinSilent().catch(err => {
            console.error('auto silent renew failed after page reload, retry renew ...', err)
            return tsgMgr.removeUser().then(() => tsgMgr.signinSilent()).catch(err => {
              reloginForOidcSessionLost()
              throw err
            })
          })
          if (tsgUser) {
            const decoded = tsgUser?.access_token ? jwtDecodeCached(tsgUser.access_token, tsg_id) : undefined
            if (decoded?.tsg_id && tsg_id === decoded.tsg_id) {
              auth.access_token = tsgUser!.access_token
              auth.access_token_decoded = decoded
            }
            else { // TODO: could mean user lose access to the TSG
              console.error('auto silent renew return different tsg_id', decoded, tsgTokenDecoded)
            }
          }
        }
      }
    }
  }
  auth.tsg_id = tsg_id
  tsgMgr.events.addUserLoaded(updateAccessTokenFn(tsg_id))
  tsgMgr.events.addSilentRenewError(retrySilentSignInWhenFailToRenewFn(tsgMgr))

  const needToWaitCustomRoles = !ftPrivateSiteTsgSupport && // no need custom roles for gcs tsg mode
    tsg_id && tsg_id !== TSG_ID_NONE && hasCustomRolesAccess(tsg_id, tsgTokenDecoded.access) // skip if no custom roles in access
  const customRolesPromise = fetchIamCustomRoleDefinitionAndCache(tsg_id).then((result) => {
    Object.defineProperty(customRolesPromise, 'resolved', { value: result })
    if (!needToWaitCustomRoles) {
      rolePermissionsPromise.then((rolePermissionDefinition) => {
        rolePermissionDefinition.customRoles = result
      })
    }
    return result
  }, (err) => {
    console.error('failed to fetch custom roles', err)
    return undefined
  })
  const tsgInstancesPromise = fetchTsgInstances(tsg_id)
  const featureFlagsPromise = fxVars.load_feature_flag_service ? loadFeatureFlags(tsg_id) : undefined
  const tsgPropertiesPromise = defaultProperitesPromise ? Promise.all([
    tsg_id === TSG_ID_NONE ? defaultProperitesPromise : fetchTsgProperties(tsg_id), defaultProperitesPromise,
  ]).then(([props, noneProps]) => handleFallbackForTsgProperties(props, noneProps, auth.access_token_decoded, tsgInstancesPromise)) : undefined
  if (!tsgsPromise && (ftPrivateSiteTsgSupport || ft_copilot_csp_mode || fxVars.use_provided_tsg_token)) {
    tsgsPromise = fetchSingleTsg(tsg_id)
  }
  const scopesPromise = fxVars.fawkes_sbac_tsgs?.includes(tsg_id) && tsg_id !== TSG_ID_NONE ? tsgInstancesPromise.then((instances) => {
    if (instances?.length) {
      return fetchIamScopeDefinitionAndCache(tsg_id).catch((err) => {
        console.error('failed to fetch scopes', err)
        return undefined
      })
    }
  }) : undefined

  await setAuthValuesAndWait({
    tsg_id: auth.tsg_id,
    tsg_name: auth.tsg_name,
    id_token: auth.id_token,
    access_token: auth.access_token,
    access_token_decoded: auth.access_token_decoded,
    user: auth.user,
  })
  authenticated.resolve(auth)
  setCurrentTsgId(tsg_id)
  __info(`user ${subname} oidc authenticated for tsg ${tsg_id}`)

  auth.instances = await tsgInstancesPromise.catch((err) => {
    setValuesInternal({
      loginFailed: {
        message: err.message,
      }
    })
    return err.instances
  })
  setDatadogGlobalContext({
    global_tsg_id: tsg_id,
    tsg_platform_region: auth.instances?.platform_region || '',
  })

  if (featureFlagsPromise) {
    auth.feature_flags = await featureFlagsPromise
  }
  const fawkesSessionPromise = fxVars.fawkes_session && tsg_id !== TSG_ID_NONE && hasRunningFawkesInst(auth.instances) ?
    handleIamFawkesSession(auth, {
      // properitesPromise,
      // preferencePromise,
      // tsgsPromise,
      tsgPropertiesPromise,
      libTokensPromise,
    } as TsgPromises).then(() => true, (err) => err || false) : false

  auth.rolePermissionDefinition = await rolePermissionsPromise
  if (needToWaitCustomRoles || 'resolved' in customRolesPromise) {
    auth.rolePermissionDefinition.customRoles = await customRolesPromise
  }
  auth.access = parseAccess(
    auth.access_token_decoded!.access,
    auth.rolePermissionDefinition,
  )
  if (tsgPropertiesPromise) {
    auth.properties = await tsgPropertiesPromise
  }
  if (scopesPromise) {
    auth.rolePermissionDefinition.scopes = await scopesPromise
  }
  if (tsgFromUrl) { // remove tsg_id from url for now, before we fully support persistent query
    cleanupTsgIdFromUrl()
  }

  if (tsgsPromise) {
    auth.tsgs = await tsgsPromise
  }
  const tsg_name = tsg_id && (auth.tsgs.get(tsg_id)?.display_name || auth.properties?.get('tsg.display_name'))
  if (tsg_name) {
    auth.tsg_name = tsg_name
  }
  else if (!auth.tsgs.has(tsg_id) && tsg_id !== TSG_ID_NONE) {
    console.error(`cannot find the tsg ${tsg_id} from the tsg tree`)
    if (auth.tsgs.isEmpty()) {
      auth.tsgs = indexTsgTree([{
        id: tsg_id,
        display_name: tsg_id,
        mocked: true,
      }], auth.tsgs.error)
    }
  }
  // console.error('tsgs', auth.tsgs)
  if (auth.sessionDataPromise) {
    const sessionData = await auth.sessionDataPromise
    if (sessionData?.logout) { // force relogin handled in promise
      return
    }
    Object.assign(auth.user!, sessionData)
    delete auth.sessionDataPromise
  }
  auth.initializing = undefined
  await setAuthValuesAndWait(auth) // ensure save tsgs and oidc session

  if (preferencePromise) {
    const prefState = await preferencePromise
    if (preference.get(COMMON_LAST_VISITED_TSG_ID) !== tsg_id && tsg_id !== TSG_ID_NONE && tsg_name) {
      preference.set(COMMON_LAST_VISITED_TSG_ID, tsg_id, { token: defaultTsgToken }).catch((err) => {
        console.error('failed to update last visited tsg_id', tsg_id, err)
      }) // no await to avoid blocking
    }
    if (!prefState.tsgs?.curernt) {
      // APPORTAL-13097 favorite items are not displayed when user logs out and logs back in
      const updatedPref = getInitialFavorites(tsg_id)
      if (updatedPref) {
        Object.assign(prefState, updatedPref)
        setValuesInternal({ preferences: prefState })
      }
    }
  }
  if (libTokensPromise) {
    await libTokensPromise
  }

  _timing.tsg_tenant_auth_load_end = Date.now()
  _timing.tsg_tenant_auth_load_duration = _timing.tsg_tenant_auth_load_end - starts
  __info(`tsg tenant, auth and preference loaded in ${_timing.tsg_tenant_auth_load_duration}ms`)
  if (_performance.abnormal) {
    disableTiming()
  }
  addDataDogTimingOnce('tsg_tenant_auth_loaded', _timing.tsg_tenant_auth_load_end)
  addDataDogTimingOnce('tsg_tenant_auth_load_duration', _timing.tsg_tenant_auth_load_duration)

  await applyDefaultRoute(auth, tsg_id, preferencePromise)

  if (fawkesSessionPromise) {
    const result = await fawkesSessionPromise
    if (result === true) {
      handleAuthorized(auth)
      return // handlded by loadMainInfo
    } // if failed, fallback to use below without fawkes
    else {
      if (result.logout) {
        if (result.message) {
          clearLoadingMask()
          setValuesInternal({
            loginFailed: {
              message: result.message,
            }
          })
        }
        else {
          logout()
        }
        return
      }
      console.error('failed to init fawkes session', result)
      let path = window.location.pathname || '/' // do not use history.location which out of sync
      if (path === '/' && fxVars.default_route) {
        path = fxVars.default_route
      }
      const fawkesPath = FAWKES_MGMT_PATH_PREFIX_REGEX.test(path)
      if (fawkesPath) {
        const errMsg = result.errorMessage || result.message
        const message = errMsg && /^[A-Z]/.test(errMsg) ? errMsg : `Failed to initialize${errMsg ? ` due to ${errMsg}` : ''}`
        setMainValues({
          fawkesSessionError: {
            from: window.location.href,
            message,
            msp: hasMicroApp(MSP_MICRO_APP_NAME) && auth.tsg_id && auth.tsgs.hasSubTenants(auth.tsg_id) ?
              fxVars.msp_route || MSP_PATH_PREFIX : undefined,
          }
        })
        history.push(PA_DEFAULT_ROUTE)
      }
    }
  }

  // load without fawkes:
  await infoLoadedWithoutFawkes(auth)

  handleAuthorized(auth)
}

const handleLogin = async (oidc: OidcState | undefined, skipLoginForRender: boolean) => { // can run mulitple times after odic relogin and app remount
  if (!oidc) {
    return // check next time
  }
  if (skipLoginForRender) {
    return // handle by render
  }
  // if (oidc.login && !oidc.oidcUser) {
  //   __info('clear session storage and login')
  //   clearOidcSessionStorage()
  //   return oidc.login()
  // }
  if (!oidc.oidcUser || !oidc.userManager) { // TODO: deal with this case later
    console.error('oidc failed to init')
    return
  }
  try {
    await handleOidcLogin(oidc) // should only run once
  }
  catch (e) {
    console.error('sparky handle oidc login failed', e)
    // TODO: add error screen
    throw e
  }
  delete sessionStorage.__sparky_path_override_key // clear after fully logged in
}

const addKeyToConfigMap = (config: Record<string, any | null>, keyField: string) => {
  for (const key of Object.keys(config)) {
    const cfg = config[key] as Record<string, string>
    if (cfg) {
      cfg[keyField] = key
    }
  }
}
export const initFxVarsForFreeze = (fxVars: FrameworkVars) => {
  if (Object.isFrozen(fxVars.oidc_client)) {
    return fxVars
  }
  if (fxVars.apps_can_launch) {
    addKeyToConfigMap(fxVars.apps_can_launch, 'app_id')
    deepFreeze(fxVars.apps_can_launch)
  }
  if (fxVars.url_overriding) {
    addKeyToConfigMap(fxVars.url_overriding, 'app_id')
    deepFreeze(fxVars.url_overriding)
  }
  if (fxVars.shell_path_overrides) {
    addKeyToConfigMap(fxVars.shell_path_overrides, 'path')
    deepFreeze(fxVars.shell_path_overrides)
  }
  deepFreeze(fxVars.oidc_client)
  deepFreeze(fxVars.api_service_urls)

  return fxVars
}

export default handleLogin
